// src/pages/TablesConfiguration/TablesConfiguration.tsx

import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    TextField,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { CreateTableData, PosTable } from "../../types/pos.types";
import { TableFormModal } from "./TableFormModal";
import { posService } from "../../../../services/pos.service";
import { facilitiesService, ListResponse } from "../../services/facilities.service";
import { useAuth } from "../../../auth";
import { Block, CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";

export const TablesConfiguration: React.FC = () => {
    const [tables, setTables] = useState<PosTable[]>([]);
    const [selectedBranch, setSelectedBranch] = useState<string>("");
    const [branches, setBranches] = useState<{ id: string; name: string }[]>([]);
    const [loadingBranches, setLoadingBranches] = useState<boolean>(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [loadingTables, setLoadingTables] = useState<boolean>(false);
    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTable, setEditingTable] = useState<PosTable | null>(null);

    const { user } = useAuth();
    const storeId = user?.store_id;

    const [showConfirmModal, setShowConfirmModal] = useState<{
        open: boolean;
        title: string;
        description: string;
        onConfirm: () => void;
    } | null>(null);

    const ConfirmModal = ({
        open,
        title,
        description,
        onConfirm,
        onCancel,
    }: {
        open: boolean;
        title: string;
        description: string;
        onConfirm: () => void;
        onCancel: () => void;
    }) => (
        <Dialog open={open} onClose={onCancel}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Typography>{description}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="primary">
                    Cancel
                </Button>
                <Button onClick={onConfirm} color="error" variant="contained">
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
    // Load branches on mount
    useEffect(() => {
        const loadBranches = async () => {
            setLoadingBranches(true);
            try {
                const response: ListResponse = await facilitiesService.listBranches({});

                const mappedBranches = response.data.map((item: any) => ({
                    id: item.id || item.branch_id,
                    name: item.name || item.branch_name,
                }));

                setBranches(mappedBranches);

                if (mappedBranches.length > 0) {
                    setSelectedBranch(mappedBranches[0].id); // Auto-select first
                }
            } catch (error) {
                console.error("Failed to load branches:", error);
                setFetchError("Could not load branches.");
            } finally {
                setLoadingBranches(false);
            }
        };

        loadBranches();
    }, []);

    useEffect(() => {
        const loadTables = async () => {
            if (!selectedBranch || !storeId) return;

            setLoadingTables(true);

            try {
                const tables = await facilitiesService.getTablesByBranchAndStore({
                    branch_id: selectedBranch,
                    store_id: storeId,
                });

                setTables(tables);
            } catch (error) {
                console.error("Failed to load tables:", error);
            } finally {
                setLoadingTables(false);
            }
        };

        loadTables();
    }, [selectedBranch, storeId]);

    // Get unique areas from current tables
    const getUniqueAreas = () =>
        Array.from(new Set(tables.map((t) => t.location.zone)));

    // Open modal to add new table
    const handleAddTable = () => {
        setEditingTable(null);
        setIsModalOpen(true);
    };

    const handleDeleteTable = async (tableId: string) => {
        setShowConfirmModal({
            open: true,
            title: "Delete Table",
            description: "Are you sure you want to delete this table?",
            onConfirm: async () => {
                setLoadingStates((prev) => ({ ...prev, [`${tableId}-delete`]: true }));
                try {
                    await facilitiesService.deleteTable(tableId);
                    setTables(tables.filter((t) => t.table_id !== tableId));
                    toast.success("Table deleted successfully");
                    setLoadingStates((prev) => ({ ...prev, [`${tableId}-delete`]: false }));
                } catch (error) {
                    console.error("Failed to delete table:", error);
                    toast.error("Failed to delete table");
                    setLoadingStates((prev) => ({ ...prev, [`${tableId}-delete`]: false }));
                }
            },
        });
    };

    const handleToggleActive = async (table: PosTable) => {
        const { table_id, is_active } = table;
        const actionKey = `${table_id}-${is_active ? "deactivate" : "activate"}`;

        setShowConfirmModal({
            open: true,
            title: is_active ? "Deactivate Table" : "Activate Table",
            description: is_active
                ? "Are you sure you want to deactivate this table?"
                : "Are you sure you want to activate this table?",
            onConfirm: async () => {
                setLoadingStates((prev) => ({ ...prev, [actionKey]: true }));
                try {
                    const updatedTable = await facilitiesService.updateTable({
                        table_id,
                        is_active: !is_active,
                        updated_at: new Date().toISOString(),
                    });

                    setTables(
                        tables.map((t) =>
                            t.table_id === updatedTable.table_id ? updatedTable : t
                        )
                    );

                    toast.success(is_active ? "Table deactivated" : "Table activated");
                    setLoadingStates((prev) => ({ ...prev, [actionKey]: false }));
                } catch (error) {
                    console.error("Failed to toggle table status:", error);
                    toast.error("Failed to update table status");
                    setLoadingStates((prev) => ({ ...prev, [actionKey]: false }));
                }
            },
        });
    };

    // Save or update table
    const handleSaveTable = async (tableData: CreateTableData) => {
        try {
            let updatedTable: PosTable;

            if (editingTable) {
                // Update existing table
                updatedTable = await facilitiesService.updateTable({
                    ...tableData,
                    table_id: editingTable.table_id,
                });

                setTables(
                    tables.map((t) => (t.table_id === updatedTable.table_id ? updatedTable : t))
                );
            } else {
                // Create new table
                updatedTable = await facilitiesService.createTable(tableData);
                setTables([...tables, updatedTable]);
            }

            setIsModalOpen(false);
        } catch (error) {
            console.error("Failed to save table:", error);
        }
    };

    const handleEditTable = (table: PosTable) => {
        setEditingTable(table);
        setIsModalOpen(true);
    };



    return (
        <>
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Table Configuration
                </Typography>

                {/* Branch Selector */}
                <FormControl fullWidth margin="normal" disabled={loadingBranches}>
                    <InputLabel id="branch-select-label">Select Branch</InputLabel>
                    {loadingBranches ? (
                        <Box display="flex" justifyContent="center" my={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : fetchError ? (
                        <Typography color="error">{fetchError}</Typography>
                    ) : (
                        <Select
                            labelId="branch-select-label"
                            value={selectedBranch}
                            label="Select Branch"
                            onChange={(e) => setSelectedBranch(e.target.value as string)}
                        >
                            {branches.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </FormControl>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddTable}
                    sx={{ mb: 2 }}
                    disabled={!selectedBranch}
                >
                    Add Table
                </Button>

                {/* Show all tables */}
                <List>
                    {tables.length === 0 && (
                        <Typography color="text.secondary">No tables added yet.</Typography>
                    )}

                    {/* Show loading spinner */}
                    {loadingTables && (
                        <Box display="flex" justifyContent="center" my={4}>
                            <CircularProgress size={32} />
                        </Box>
                    )}

                    {/* Show all tables only when not loading */}
                    {!loadingTables && tables.length > 0 && (
                        <List>
                            {tables.map((table) => {
                                const isTableLoading = loadingStates[table.table_id];

                                return (
                                    <Paper key={table.table_id} sx={{ mb: 2, p: 2 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <ListItemText
                                                primary={`${table.table_name} (${table.table_number})`}
                                                secondary={`Capacity: ${table.capacity}, Area: ${table.location?.zone}`}
                                            />
                                            <Box>
                                                <IconButton onClick={() => handleEditTable(table)}>
                                                    <EditIcon color="action" />
                                                </IconButton>

                                                <Tooltip title={table.is_active ? "Deactivate" : "Activate"} arrow>
                                                    <span>
                                                        <IconButton
                                                            onClick={() => handleToggleActive(table)}
                                                            disabled={loadingStates[`${table.table_id}-${table.is_active ? "deactivate" : "activate"}`]}
                                                        >
                                                            {loadingStates[`${table.table_id}-${table.is_active ? "deactivate" : "activate"}`] ? (
                                                                <CircularProgress size={24} color="warning" />
                                                            ) : table.is_active ? (
                                                                <Block color="warning" />
                                                            ) : (
                                                                <CheckCircle color="success" />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <IconButton
                                                    onClick={() => handleDeleteTable(table.table_id)}
                                                    disabled={loadingStates[`${table.table_id}-delete`]}
                                                >
                                                    {loadingStates[`${table.table_id}-delete`] ? (
                                                        <CircularProgress size={24} color="error" />
                                                    ) : (
                                                        <DeleteIcon color="error" />
                                                    )}
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </List>
                    )}

                    {/* No tables message */}
                    {!loadingTables && tables.length === 0 && (
                        <Typography color="text.secondary">No tables found for this branch.</Typography>
                    )}
                </List>

                {/* Modal */}
                <TableFormModal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={handleSaveTable}
                    initialData={editingTable} // Already passed ✅
                    existingAreas={getUniqueAreas()}
                    branchId={selectedBranch}
                    storeId={storeId}
                />
            </Box>
            {showConfirmModal && (
                <ConfirmModal
                    open={showConfirmModal.open}
                    title={showConfirmModal.title}
                    description={showConfirmModal.description}
                    onConfirm={() => {
                        showConfirmModal.onConfirm();
                        setShowConfirmModal(null);
                    }}
                    onCancel={() => setShowConfirmModal(null)}
                />
            )}
        </>
    );
};